/**
 * Utilities for building URLs to external observability tools (Datadog, Sentry)
 */

interface DatadogLogsParams {
  applicantId?: string
  companyId?: string
  employeeId?: string
  jobListingId?: string
  applicationId?: string
}

/**
 * Build a Datadog Logs URL with filters
 * @param params - Object containing optional applicantId and companyId
 * @returns Datadog logs URL with query filters and 3-day time range
 */
export const buildDatadogLogsUrl = (params: DatadogLogsParams): string => {
  const filters = Object.entries(params)
    .filter(([_, v]) => v)
    .map(([k, v]) => `@${k}:${v}`)
    .join(' ')

  const searchParams = new URLSearchParams({
    query: filters,
    from_ts: String(Date.now() - 3 * 24 * 60 * 60 * 1000),
    to_ts: String(Date.now()),
    live: 'true',
  })

  return `https://app.datadoghq.com/logs?${searchParams.toString()}`
}

/**
 * Build a Sentry Issues URL filtered by applicantId
 * @param applicantId - The applicant ID to filter by
 * @returns Sentry issues URL with query filter
 */
export const buildSentryUrl = (applicantId: string): string => {
  return `https://sentry.io/organizations/double-nickel/issues/?query=applicantId:${encodeURIComponent(applicantId)}`
}

/**
 * Build an Inngest dashboard URL
 * @returns Inngest dashboard URL for the double-nickel app
 */
export const buildInngestUrl = (): string => {
  return `https://app.inngest.com/env/production/apps/double-nickel/functions`
}

/**
 * Build an Auth0 user search URL filtered by phone number
 * @param phone - The phone number to search for
 * @returns Auth0 dashboard URL with phone search
 */
export const buildAuth0Url = (phone: string): string => {
  return `https://manage.auth0.com/dashboard/us/double-nickel/users?q=${encodeURIComponent(phone)}`
}
